import { Question, ParsedRubric, CSVRow, Submission, SubmissionCSVRow } from './types';

/**
 * Parse a formatted rubric string into separate low, medium, and high rubrics
 */
export const parseRubric = (rubricString: string): ParsedRubric => {
  if (!rubricString) return { low: '', medium: '', high: '' };

  // Split the rubric by the section headers
  const sections = rubricString.split(/(?=Low Mark Range|Medium Mark Range|High Mark Range)/i);
  
  let lowRubric = '';
  let mediumRubric = '';
  let highRubric = '';

  sections.forEach(section => {
    const trimmedSection = section.trim();
    if (trimmedSection.match(/^Low Mark Range/i)) {
      // Extract content after the header line
      const lines = trimmedSection.split('\n');
      lowRubric = lines.slice(1).join(' ').trim();
    } else if (trimmedSection.match(/^Medium Mark Range/i)) {
      const lines = trimmedSection.split('\n');
      mediumRubric = lines.slice(1).join(' ').trim();
    } else if (trimmedSection.match(/^High Mark Range/i)) {
      const lines = trimmedSection.split('\n');
      highRubric = lines.slice(1).join(' ').trim();
    }
  });

  return { low: lowRubric, medium: mediumRubric, high: highRubric };
};

/**
 * Convert questions to CSV format
 */
export const convertQuestionsToCSV = (questions: Question[]): string => {
  const headers = [
    'Question',
    'Marks',
    'Low Mark Range',
    'Low Rubric',
    'Medium Mark Range',
    'Medium Rubric',
    'High Mark Range',
    'High Rubric',
    'Created Date'
  ];

  const csvRows: string[] = [headers.join(',')];

  questions.forEach(question => {
    const parsedRubric = parseRubric(question.rubric);
    const formattedDate = new Date(question.createdAt).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    const row: CSVRow = {
      question: question.question,
      marks: question.marks,
      lowMarkRange: question.lowMarkRange || '',
      lowRubric: parsedRubric.low,
      mediumMarkRange: question.mediumMarkRange || '',
      mediumRubric: parsedRubric.medium,
      highMarkRange: question.highMarkRange || '',
      highRubric: parsedRubric.high,
      createdAt: formattedDate
    };

    // Escape and format each field for CSV
    const csvRow = [
      escapeCSVField(row.question),
      row.marks.toString(),
      escapeCSVField(row.lowMarkRange),
      escapeCSVField(row.lowRubric),
      escapeCSVField(row.mediumMarkRange),
      escapeCSVField(row.mediumRubric),
      escapeCSVField(row.highMarkRange),
      escapeCSVField(row.highRubric),
      escapeCSVField(row.createdAt)
    ];

    csvRows.push(csvRow.join(','));
  });

  return csvRows.join('\n');
};

/**
 * Escape CSV field by wrapping in quotes and escaping internal quotes
 */
const escapeCSVField = (field: string): string => {
  if (!field) return '';
  
  // Convert to string and handle special characters
  let escaped = field.toString();
  
  // Replace any carriage returns and normalize line breaks
  escaped = escaped.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  
  // Escape quotes by doubling them
  escaped = escaped.replace(/"/g, '""');
  
  // Always wrap in quotes to ensure proper CSV formatting
  return `"${escaped}"`;
};

/**
 * Download CSV file
 */
export const downloadCSV = (csvContent: string, filename: string = 'questions-export.csv'): void => {
  // Add UTF-8 BOM for better Excel compatibility
  const BOM = '\uFEFF';
  const csvWithBOM = BOM + csvContent;
  
  const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Filter questions by selected IDs
 */
export const filterSelectedQuestions = (questions: Question[], selectedIds: string[]): Question[] => {
  return questions.filter(question => selectedIds.includes(question._id));
};

// Submission-specific utility functions

/**
 * Calculate total score and marks for a submission
 */
const calculateSubmissionTotals = (submissions: Array<{marks: number; score: number}>) => {
  const totalMarks = submissions.reduce((sum, sub) => sum + (sub.marks || 0), 0);
  const totalScore = submissions.reduce((sum, sub) => sum + (sub.score || 0), 0);
  return { totalScore, totalMarks };
};

/**
 * Check if submission is flagged for inappropriate content
 */
const isSubmissionFlagged = (submissions: Array<{contentModeration?: {isInappropriate: boolean}}>) => {
  return submissions.some(sub => sub.contentModeration?.isInappropriate === true);
};

/**
 * Convert submissions to CSV format
 */
export const convertSubmissionsToCSV = (submissions: Submission[]): string => {
  const headers = [
    'Student Name',
    'Student Email',
    'Class Name',
    'Submitted Date',
    'Total Score',
    'Total Marks',
    'Percentage',
    'Question Count',
    'Flagged for Review'
  ];

  const csvRows: string[] = [headers.join(',')];

  submissions.forEach(submission => {
    const { totalScore, totalMarks } = calculateSubmissionTotals(submission.submissions);
    const percentage = totalMarks > 0 ? ((totalScore / totalMarks) * 100).toFixed(1) : '0.0';
    const isFlagged = isSubmissionFlagged(submission.submissions);
    
    const formattedDate = new Date(submission.submittedAt).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    const row: SubmissionCSVRow = {
      studentName: submission.student.name,
      studentEmail: submission.student.email,
      className: submission.class.name,
      submittedAt: formattedDate,
      totalScore: totalScore,
      totalMarks: totalMarks,
      percentage: `${percentage}%`,
      questionCount: submission.submissions.length,
      isFlagged: isFlagged ? 'Yes' : 'No'
    };

    // Escape and format each field for CSV
    const csvRow = [
      escapeCSVField(row.studentName),
      escapeCSVField(row.studentEmail),
      escapeCSVField(row.className),
      escapeCSVField(row.submittedAt),
      row.totalScore.toString(),
      row.totalMarks.toString(),
      escapeCSVField(row.percentage),
      row.questionCount.toString(),
      escapeCSVField(row.isFlagged)
    ];

    csvRows.push(csvRow.join(','));
  });

  return csvRows.join('\n');
};

/**
 * Filter submissions by selected IDs
 */
export const filterSelectedSubmissions = (submissions: Submission[], selectedIds: string[]): Submission[] => {
  return submissions.filter(submission => selectedIds.includes(submission.id));
}; 